/**
 * SportsPay hands its hosted registration a `CallbackURL` pointing back at OUR
 * org page (`…/organization/{id}?origin=onboarding_sportspay`). That is a
 * top-level redirect by design — but we open the registration in an iframe, so
 * the callback loads our own app INSIDE that frame instead.
 *
 * Because it is our page and our origin, it can announce completion itself:
 * the framed copy posts a message up to the parent, which closes the frame and
 * re-reads the organization. No backend or SportsPay change is needed.
 */

/** Query param SportsPay's callback carries back to the org page. */
export const SPORTS_PAY_CALLBACK_PARAM = 'origin'

/** Its value when the registration flow is what sent us here. */
export const SPORTS_PAY_CALLBACK_VALUE = 'onboarding_sportspay'

/** Message type posted from the framed callback page up to the opener. */
export const SPORTS_PAY_COMPLETE_MESSAGE = 'sportspay:complete'

export interface SportsPayCompleteMessage {
  type: typeof SPORTS_PAY_COMPLETE_MESSAGE
  /**
   * Every query param SportsPay put on the callback, forwarded verbatim.
   *
   * We build the CallbackURL with only `origin`, but SportsPay is expected to
   * append its own (a merchant id, a status) when it actually redirects. Since
   * we cannot see that until it fires, the whole bag is carried up rather than
   * a guessed subset — the parent logs it and picks out what it needs.
   */
  params?: Record<string, string>
}

/** Collects a query string into a plain object, for forwarding and logging. */
export const readSportsPayCallbackParams = (
  search: string
): Record<string, string> => {
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  )
  const out: Record<string, string> = {}
  params.forEach((value, key) => {
    out[key] = value
  })
  return out
}

/**
 * Whether a URL's query string is SportsPay's completion callback.
 *
 * Takes the raw search string so it is testable without a browser and works
 * identically wherever it is called from.
 */
export const isSportsPayCallback = (search: string): boolean => {
  if (!search) {
    return false
  }
  const params = new URLSearchParams(
    search.startsWith('?') ? search.slice(1) : search
  )
  return params.get(SPORTS_PAY_CALLBACK_PARAM) === SPORTS_PAY_CALLBACK_VALUE
}

/**
 * Whether an incoming `message` event is our completion signal.
 *
 * Checks the ORIGIN as well as the payload: `message` events arrive from any
 * frame on the page, including third-party ones, so an unvalidated listener
 * would let anything embedded here close the modal and trigger a refetch.
 */
export const isSportsPayCompleteMessage = (
  event: { origin?: string; data?: unknown },
  expectedOrigin: string
): boolean => {
  if (event.origin !== expectedOrigin) {
    return false
  }
  const data = event.data as SportsPayCompleteMessage | undefined
  return data?.type === SPORTS_PAY_COMPLETE_MESSAGE
}

/**
 * The fields the account-level onboarding PUT needs, pulled out of the params
 * SportsPay appends to the callback.
 *
 * Their `status` arrives hyphenated (`in-review`); the API expects underscores,
 * exactly as the account-settings flow normalizes it.
 *
 * Returns `undefined` when either field is missing — the caller must not send a
 * half-built body.
 */
export interface SportsPayCompletionFields {
  terminalId: string
  status: string
}

export const readSportsPayCompletionFields = (
  params: Record<string, string> | undefined
): SportsPayCompletionFields | undefined => {
  const terminalId = params?.code?.trim()
  const status = params?.status?.trim()
  if (!terminalId || !status) {
    return undefined
  }
  return { terminalId, status: status.replace(/-/g, '_') }
}
